import React, { useState, useEffect } from 'react';
import { meetingService } from '../services/api';

const EMPTY_MEETING = {
  meeting_title: '',
  meeting_agenda: '',
  meeting_date: '',
  meeting_time: '',
  duration_minutes: 60,
  meeting_link: '',
  attendees_customer: '',
  attendees_internal: '',
  attendees_partner: ''
};

export default function Meetings({ opportunityId, opportunityName, customerEmail }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_MEETING);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadMeetings();
  }, [opportunityId]);

  const loadMeetings = () => {
    setLoading(true);
    meetingService.getAll(opportunityId)
      .then(data => setMeetings(data.meetings || []))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load meetings' }))
      .finally(() => setLoading(false));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    try {
      await meetingService.create(opportunityId, {
        ...form,
        opportunity_id: opportunityId
      });
      setMessage({ type: 'success', text: 'Meeting scheduled! Invites sent to all attendees.' });
      setForm(EMPTY_MEETING);
      setShowForm(false);
      loadMeetings();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to schedule meeting' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="meetings-container">
      <div className="meetings-header">
        <h3>Meetings</h3>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Schedule Meeting'}
        </button>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {showForm && (
        <div className="meeting-form-card">
          <h4>Schedule New Meeting</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Meeting Title *</label>
              <input
                name="meeting_title"
                value={form.meeting_title}
                onChange={handleChange}
                required
                placeholder="e.g., Discovery Call, Technical Review"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="meeting_date"
                  value={form.meeting_date}
                  onChange={handleChange}
                  min={getTodayDate()}
                  required
                />
              </div>
              <div className="form-group">
                <label>Time *</label>
                <input
                  type="time"
                  name="meeting_time"
                  value={form.meeting_time}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Duration (minutes)</label>
                <select name="duration_minutes" value={form.duration_minutes} onChange={handleChange}>
                  <option value="30">30 min</option>
                  <option value="60">60 min</option>
                  <option value="90">90 min</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Meeting Link (optional)</label>
              <input
                name="meeting_link"
                value={form.meeting_link}
                onChange={handleChange}
                placeholder="https://meet.google.com/... or Zoom link"
              />
            </div>

            <div className="form-group">
              <label>Agenda</label>
              <textarea
                name="meeting_agenda"
                value={form.meeting_agenda}
                onChange={handleChange}
                rows="4"
                placeholder="Meeting agenda and discussion points..."
              />
            </div>

            <div className="form-group">
              <label>Customer Attendees (emails, comma-separated)</label>
              <input
                name="attendees_customer"
                value={form.attendees_customer}
                onChange={handleChange}
                placeholder={customerEmail || 'customer@company.com, cto@company.com'}
              />
            </div>

            <div className="form-group">
              <label>Cloud Kinetics Attendees (emails, comma-separated)</label>
              <input
                name="attendees_internal"
                value={form.attendees_internal}
                onChange={handleChange}
                placeholder="rajesh.j@cloud-kinetics.com"
              />
            </div>

            <div className="form-group">
              <label>Partner Attendees (emails, comma-separated)</label>
              <input
                name="attendees_partner"
                value={form.attendees_partner}
                onChange={handleChange}
                placeholder="partner@company.com"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Scheduling...' : 'Schedule & Send Invites'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading meetings...</div>
      ) : meetings.length === 0 ? (
        <div className="no-data">No meetings scheduled yet</div>
      ) : (
        <div className="meetings-list">
          {meetings.map(meeting => (
            <div key={meeting.meeting_id} className="meeting-card">
              <div className="meeting-header">
                <h4>{meeting.meeting_title}</h4>
                <span className={`badge badge-${meeting.status}`}>{meeting.status}</span>
              </div>
              <div className="meeting-details">
                <div className="meeting-detail">
                  <strong>Date & Time:</strong> {formatDateTime(meeting.meeting_date_time)}
                </div>
                <div className="meeting-detail">
                  <strong>Duration:</strong> {meeting.duration_minutes} minutes
                </div>
                {meeting.meeting_link && (
                  <div className="meeting-detail">
                    <strong>Link:</strong> <a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer">Join Meeting</a>
                  </div>
                )}
              </div>
              {meeting.meeting_agenda && (
                <div className="meeting-agenda">
                  <strong>Agenda:</strong>
                  <p>{meeting.meeting_agenda}</p>
                </div>
              )}
              <div className="meeting-attendees">
                {meeting.attendees.customer.length > 0 && (
                  <div className="attendee-group">
                    <strong>Customer:</strong> {meeting.attendees.customer.join(', ')}
                  </div>
                )}
                {meeting.attendees.internal.length > 0 && (
                  <div className="attendee-group">
                    <strong>CK Team:</strong> {meeting.attendees.internal.join(', ')}
                  </div>
                )}
                {meeting.attendees.partner.length > 0 && (
                  <div className="attendee-group">
                    <strong>Partner:</strong> {meeting.attendees.partner.join(', ')}
                  </div>
                )}
              </div>
              <div className="meeting-footer">
                <small>Scheduled by {meeting.created_by} on {new Date(meeting.created_at).toLocaleDateString()}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
